import { z } from 'zod';

export type SensorType = z.infer<typeof sensorTypeSchema>;
export const sensorTypeSchema = z.union([
  z.literal('wood'),
  z.literal('sensorPro'),
  z.literal('sensorPlus'),
  z.literal('bracket'),
  z.literal('sensor'),
]);

const normalizeWoodType = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }

  const normalizedValue = value.trim().toLowerCase().replace(/[_-]+/g, ' ');

  switch (normalizedValue) {
    case 'spruce':
      return 'nordic spruce';
    case 'pine':
      return 'nordic pine';
    case 'nordic spruce':
      return 'nordic spruce';
    case 'nordic pine':
      return 'nordic pine';
    case 'nz radiata pine':
      return 'nz radiata pine';
    default:
      return value;
  }
};

export type WoodType = z.infer<typeof woodTypeSchema>;
export const woodTypeSchema = z.preprocess(
  normalizeWoodType,
  z.union([z.literal('nordic spruce'), z.literal('nordic pine'), z.literal('nz radiata pine')])
);

export const woodScrewLengthMmSchema = z.number().int().min(1).max(300);

export type SensorPosition = z.infer<typeof positionSchema>;
export const positionSchema = z.union([
  z.literal('onCeiling'),
  z.literal('onRoof'),
  z.literal('onWall'),
  z.literal('onFloor'),
  z.literal('insideRoof'),
  z.literal('insideCeiling'),
  z.literal('insideWall'),
  z.literal('insideFloor'),
  z.literal('insideBox'),
]);

export type SensorStatus = z.infer<typeof sensorStatusSchema>;
export const sensorStatusSchema = z.union([
  z.literal('ok'),
  z.literal('warning'),
  z.literal('error'),
  z.literal('inactive'),
]);
export const sensorModeSchema = z.union([
  z.literal('instant'),
  z.literal('normal'),
  z.literal('wood'),
]);
export const temperatureAlertLevelSchema = z.union([
  z.literal('off'),
  z.literal('freeze'),
  z.literal('refrigerator'),
  z.literal('fire'),
]);

export type Sensor = z.infer<typeof sensorSchema>;
export const sensorSchema = z.object({
  sensorId: z.number(),
  sensorLocalId: z.number(),
  installationId: z.number().nullable(),
  productKey: z.string().nullable().optional(),
  removedSensorProductId: z.number().nullable().optional(),
  projectId: z.number(),
  projectName: z.string().nullable().optional(),
  sensorType: sensorTypeSchema,
  sensorMode: sensorModeSchema,
  position: positionSchema,
  status: sensorStatusSchema,
  highestStatus: sensorStatusSchema.nullable().optional(),
  coordinatesX: z.number().nullable(),
  coordinatesY: z.number().nullable(),
  isWaterguard: z.boolean(),
  isActive: z.boolean(),
  temperatureAlertLevel: temperatureAlertLevelSchema.nullable().optional(),
  woodType: woodTypeSchema.nullable().optional(),
  woodScrewLengthMm: woodScrewLengthMmSchema.nullable().optional(),
  woodInsulatedElectrode: z.boolean().nullable().optional(),
  label: z.string(),
  comment: z.string(),
});
